import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Cpu, Users, KeyRound, Home, RotateCcw, Share2, Copy, Trophy, Medal } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";
import { sfx } from "../../lib/sound";

function rng(seed, k, salt) {
  let t = (seed + k * 374761393 + salt * 40503) >>> 0;
  t = (t + 0x6D2B79F5) >>> 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

export default function Dinorace() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const C = theme.colors;
  const [params] = useSearchParams();
  const joinParam = params.get("code");

  const [view, setView] = useState(joinParam ? "loading" : "menu"); // menu|loading|lobby|race|result|board
  const [mode, setMode] = useState("solo");
  const [pub, setPub] = useState(null);          // server state
  const [result, setResult] = useState(null);
  const [scores, setScores] = useState({ me: 0, opp: 0 });
  const [countLabel, setCountLabel] = useState(null);
  const [crashedWait, setCrashedWait] = useState(false);
  const [board, setBoard] = useState(null);
  const [joinInput, setJoinInput] = useState("");
  const [busy, setBusy] = useState(false);

  const canvasRef = useRef(null);
  const dim = useRef({ W: 400, H: 700, dpr: 1 });
  const seedRef = useRef(1);
  const gameIdRef = useRef(null);
  const startMsRef = useRef(0);     // local ms timestamp for GO
  const offsetRef = useRef(0);      // server-local clock offset (ms)
  const obsRef = useRef([]);
  const meRef = useRef(null);
  const oppRef = useRef(null);
  const rafRef = useRef(0);
  const pollRef = useRef(0);
  const finishedRef = useRef(false);
  const crashedWaitRef = useRef(false);
  const viewRef = useRef(view); viewRef.current = view;

  useEffect(() => { if (joinParam) doJoin(joinParam); }, []); // eslint-disable-line

  const sizeCanvas = () => {
    const cv = canvasRef.current; if (!cv) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
    dim.current = { W, H, dpr };
  };

  // ---- world ---- (Chrome-dino-achtige schaal: kleine dino + obstakels, ruim vooruitzicht)
  const speedAt = (t) => { const { W } = dim.current; return Math.min(W * 1.15, W * 0.60 + W * 0.016 * t); };
  const distAt = (t) => { const { W } = dim.current; const v0 = W * 0.60, a = W * 0.016, vc = W * 1.15; const tc = (vc - v0) / a; if (t <= tc) return v0 * t + 0.5 * a * t * t; return (v0 * tc + 0.5 * a * tc * tc) + vc * (t - tc); };
  const scoreUnit = () => dim.current.W * 0.05;

  const ensureObs = (uptoX) => {
    const { W, H } = dim.current; const laneH = H / 2; const arr = obsRef.current;
    let lastX = arr.length ? arr[arr.length - 1].x : W * 1.7;
    let k = arr.length;
    while (lastX < uptoX) {
      const sp = W * (0.58 + rng(seedRef.current, k, 1) * 0.55);
      const x = lastX + sp;
      const r = rng(seedRef.current, k, 2);
      if (r < 0.76) { const big = rng(seedRef.current, k, 3); arr.push({ x, type: "cactus", w: laneH * (0.04 + big * 0.035), h: laneH * (0.10 + big * 0.09) }); }
      else { arr.push({ x, type: "bird", w: laneH * 0.12, h: laneH * 0.07, by: laneH * (0.20 + rng(seedRef.current, k, 4) * 0.06) }); }
      lastX = x; k++;
    }
  };

  const newDino = () => ({ y: 0, vy: 0, ground: true, alive: true, crashT: 0, jumpIdx: 0 });

  const resetRace = () => {
    obsRef.current = [];
    meRef.current = newDino();
    oppRef.current = newDino();
    finishedRef.current = false;
    crashedWaitRef.current = false;
    countRef.current = null;
    scoresRef.current = { me: 0, opp: 0 };
    setCrashedWait(false);
    setScores({ me: 0, opp: 0 });
  };

  const laneGeom = (laneH) => ({
    groundY: laneH * 0.82, dinoX: dim.current.W * 0.10,
    dinoW: laneH * 0.115, dinoH: laneH * 0.145, gy: laneH * 4.6, jumpV: laneH * 2.0,
  });

  const stepDino = (d, t, taps, isAI) => {
    const { H } = dim.current; const laneH = H / 2; const g = laneGeom(laneH);
    if (!d.alive) return;
    const D = distAt(t);
    ensureObs(D + dim.current.W * 1.5);

    // input
    if (isAI === "ai") {
      // springt alleen voor cactussen; vogels vliegen hoog → eronderdoor (niet springen)
      if (d.ground) {
        const react = Math.max(laneH * 0.6, speedAt(t) * (0.38 + rng(seedRef.current, Math.floor(t * 3), 7) * 0.06));
        for (const o of obsRef.current) {
          const ahead = o.x - D;
          if (ahead >= react) break;
          if (ahead > -g.dinoW && o.type === "cactus") { d.vy = -g.jumpV; d.ground = false; break; }
        }
      }
    } else if (taps) {
      while (d.jumpIdx < taps.length && taps[d.jumpIdx] <= t) {
        if (d.ground) { d.vy = -g.jumpV; d.ground = false; }
        d.jumpIdx++;
      }
    }

    // physics
    if (!d.ground) {
      d.vy += g.gy * (1 / 60);
      d.y += d.vy * (1 / 60);
      if (d.y >= 0) { d.y = 0; d.vy = 0; d.ground = true; }
    }

    // collision
    const dt = g.groundY - g.dinoH + d.y, db = g.groundY + d.y;
    for (const o of obsRef.current) {
      const sx = g.dinoX + (o.x - D);
      if (sx + o.w < g.dinoX - g.dinoW) continue;
      if (sx > g.dinoX + g.dinoW + 4) break;
      if (sx < g.dinoX + g.dinoW && sx + o.w > g.dinoX) {
        let oy0, oy1;
        if (o.type === "cactus") { oy0 = g.groundY - o.h; oy1 = g.groundY; }
        else { oy1 = g.groundY - o.by; oy0 = oy1 - o.h; }
        if (db > oy0 && dt < oy1) { d.alive = false; d.crashT = t; sfx.wrong(); }
      }
    }
  };

  // ---- drawing ----
  const drawDinoShape = (ctx, x, baseY, w, h, fg, run, dead) => {
    ctx.fillStyle = fg;
    const px = w / 4;
    // body
    ctx.fillRect(x, baseY - h * 0.55, w * 0.7, h * 0.45);
    // tail
    ctx.fillRect(x - px * 0.8, baseY - h * 0.5, px, h * 0.18);
    // head
    ctx.fillRect(x + w * 0.45, baseY - h, w * 0.55, h * 0.5);
    ctx.fillRect(x + w * 0.9, baseY - h * 0.78, w * 0.18, h * 0.2);
    // eye (bg dot)
    ctx.clearRect(x + w * 0.78, baseY - h * 0.86, px * 0.4, px * 0.4);
    // legs
    if (dead) { ctx.fillRect(x + w * 0.1, baseY - h * 0.1, w * 0.2, h * 0.1); ctx.fillRect(x + w * 0.45, baseY - h * 0.1, w * 0.2, h * 0.1); }
    else if (run) { ctx.fillRect(x + w * 0.12, baseY - h * 0.12, w * 0.18, h * 0.12); }
    else { ctx.fillRect(x + w * 0.4, baseY - h * 0.12, w * 0.18, h * 0.12); }
  };

  const drawLane = (ctx, oy, laneH, d, t, fg, bg, ground, label, score, crashed, showCue) => {
    const { W } = dim.current; const g = laneGeom(laneH);
    const night = Math.floor(t / 120) % 2 === 1;
    const sky1 = night ? "#241a3a" : "#fbe9c0";
    const sky2 = night ? "#3a2f57" : "#f3d79a";
    const sand = night ? "#2a2140" : "#e7c489";
    const dune = night ? "#322748" : "#dcb672";
    const charCol = night ? "#ece6f5" : "#5a4632";
    const cactusCol = night ? "#3f8a76" : "#4f9a52";
    const D = distAt(t);
    ctx.save(); ctx.translate(0, oy);
    // lucht
    const sky = ctx.createLinearGradient(0, 0, 0, laneH); sky.addColorStop(0, sky1); sky.addColorStop(1, sky2);
    ctx.fillStyle = sky; ctx.fillRect(0, 0, W, laneH);
    // zon / maan
    const sunR = laneH * 0.06;
    ctx.fillStyle = night ? "#d9d2ec" : "#fff3cf";
    ctx.beginPath(); ctx.arc(W * 0.82, laneH * 0.2, sunR, 0, 7); ctx.fill();
    if (night) { ctx.fillStyle = sky1; ctx.beginPath(); ctx.arc(W * 0.85, laneH * 0.18, sunR, 0, 7); ctx.fill(); }
    // woestijn-duinen (parallax)
    ctx.fillStyle = dune;
    const doff = (D * 0.15) % (W * 0.8);
    for (let i = -1; i < 3; i++) {
      const cx = i * W * 0.8 - doff + W * 0.4;
      ctx.beginPath(); ctx.ellipse(cx, g.groundY + laneH * 0.04, W * 0.5, laneH * 0.16, 0, Math.PI, 0); ctx.fill();
    }
    // zandgrond
    ctx.fillStyle = sand; ctx.fillRect(0, g.groundY, W, laneH - g.groundY);
    // grondlijn
    ctx.strokeStyle = charCol; ctx.globalAlpha = 0.55; ctx.lineWidth = 2; ctx.setLineDash([7, 5]);
    ctx.beginPath(); ctx.moveTo(0, g.groundY + 2); ctx.lineTo(W, g.groundY + 2); ctx.stroke(); ctx.setLineDash([]); ctx.globalAlpha = 1;
    // obstakels
    for (const o of obsRef.current) {
      const sx = g.dinoX + (o.x - D);
      if (sx + o.w < -10 || sx > W + 10) continue;
      if (o.type === "cactus") {
        ctx.fillStyle = cactusCol;
        ctx.fillRect(sx, g.groundY - o.h, o.w, o.h);
        ctx.fillRect(sx - o.w * 0.5, g.groundY - o.h * 0.55, o.w * 0.5, o.h * 0.28);
        ctx.fillRect(sx + o.w, g.groundY - o.h * 0.65, o.w * 0.5, o.h * 0.3);
      } else {
        const by = g.groundY - o.by - o.h;
        const flap = Math.floor(t * 6) % 2 === 0;
        ctx.fillStyle = charCol;
        ctx.fillRect(sx, by + (flap ? 0 : o.h * 0.4), o.w, o.h * 0.4);
        ctx.fillRect(sx + o.w * 0.3, by + o.h * 0.3, o.w * 0.7, o.h * 0.35);
      }
    }
    // dino
    const baseY = g.groundY + d.y;
    drawDinoShape(ctx, g.dinoX, baseY, g.dinoW, g.dinoH, charCol, Math.floor(t * 12) % 2 === 0 && d.ground, crashed);

    // cue: cactus = SPRING (groen), vogel = NIET SPRINGEN/eronderdoor (rood)
    if (showCue && d.alive) {
      let near = null;
      for (const o of obsRef.current) { const a = o.x - D; if (a > -g.dinoW) { near = { o, a }; break; } }
      if (near && near.a < W * 0.98) {
        const isBird = near.o.type === "bird";
        const sx = g.dinoX + near.a;
        const topY = isBird ? g.groundY - near.o.by - near.o.h : g.groundY - near.o.h;
        const bob = Math.sin(t * 8) * laneH * 0.02;
        const col = isBird ? "#e0405b" : "#2bb673";
        const cx = sx + near.o.w / 2, cy = topY - laneH * 0.08 + bob, s = laneH * 0.04;
        ctx.fillStyle = col;
        if (isBird) { // omhoog pijl = NIET hier omhoog
          ctx.beginPath(); ctx.moveTo(cx, cy - s); ctx.lineTo(cx - s, cy + s * 0.6); ctx.lineTo(cx + s, cy + s * 0.6); ctx.closePath(); ctx.fill();
        } else {
          ctx.beginPath(); ctx.moveTo(cx - s, cy - s); ctx.lineTo(cx + s, cy - s); ctx.lineTo(cx, cy + s * 0.7); ctx.closePath(); ctx.fill();
        }
        const dIdeal = speedAt(t) * (g.jumpV / g.gy);
        const inWindow = isBird ? near.a < dIdeal * 2.2 : (d.ground && near.a > dIdeal * 0.7 && near.a < dIdeal * 1.5);
        if (inWindow) {
          ctx.globalAlpha = 0.6 + 0.4 * Math.abs(Math.sin(t * 11));
          ctx.fillStyle = col; ctx.font = `bold ${laneH * 0.085}px monospace`; ctx.textAlign = "center"; ctx.textBaseline = "bottom";
          ctx.fillText(isBird ? "NIET SPRINGEN!" : "SPRING!", W * 0.46, laneH * 0.30);
          ctx.globalAlpha = 1;
        }
      }
    }
    // label + score
    ctx.fillStyle = charCol; ctx.globalAlpha = 0.9;
    ctx.font = `bold ${laneH * 0.07}px monospace`; ctx.textAlign = "left"; ctx.textBaseline = "top";
    ctx.fillText(label, 12, 10);
    ctx.textAlign = "right"; ctx.fillText(String(score).padStart(5, "0"), W - 12, 10);
    if (crashed) { ctx.textAlign = "center"; ctx.font = `bold ${laneH * 0.12}px monospace`; ctx.fillText("BOEM!", W / 2, laneH * 0.42); }
    ctx.globalAlpha = 1;
    ctx.restore();
  };

  const draw = () => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    const { W, H, dpr } = dim.current; const laneH = H / 2;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const now = Date.now();
    const t = Math.max(0, (now - startMsRef.current) / 1000);
    const night = Math.floor(t / 120) % 2 === 1;
    const fg = night ? "#e8eef7" : "#3a3f4b";
    const bg = night ? "#1f2533" : "#f3f5f8";

    const me = meRef.current, opp = oppRef.current;
    const meT = me.alive ? t : me.crashT;
    const oppT = opp.alive ? t : opp.crashT;

    drawLane(ctx, 0, laneH, me, meT, fg, bg, true, "JIJ", scoresRef.current.me, !me.alive, true);
    // divider
    ctx.fillStyle = night ? "#0d111b" : "#d2d8e2"; ctx.fillRect(0, laneH - 3, W, 6);
    drawLane(ctx, laneH, laneH, opp, oppT, fg, bg, true, mode === "solo" ? "COMPUTER" : (pub?.opp_name || "VRIEND").toUpperCase(), scoresRef.current.opp, !opp.alive, false);
  };

  // ---- main loop ----
  useEffect(() => {
    if (view !== "race") return;
    sizeCanvas();
    const loop = () => {
      const now = Date.now();
      const tRaw = (now - startMsRef.current) / 1000;
      if (tRaw < 0) {
        const c = Math.ceil(-tRaw);
        const lbl = c > 0 ? String(c) : null;
        if (countRef.current !== lbl) { countRef.current = lbl; setCountLabel(lbl); if (lbl) sfx.beep(); }
        draw();
        rafRef.current = requestAnimationFrame(loop);
        return;
      }
      const lbl = tRaw < 0.7 ? "GO" : null;
      if (countRef.current !== lbl) { countRef.current = lbl; setCountLabel(lbl); if (lbl === "GO") sfx.go(); }

      const t = tRaw;
      const me = meRef.current, opp = oppRef.current;
      // step physics ~ fixed
      stepDino(me, t, jumpsRef.current, "me");
      if (mode === "solo") stepDino(opp, t, null, "ai");
      else reconstructOpp(opp, t);

      const su = scoreUnit();
      const meScore = Math.floor(distAt(me.alive ? t : me.crashT) / su);
      const oppScore = mode === "solo" ? Math.floor(distAt(opp.alive ? t : opp.crashT) / su) : (oppRef.current.serverScore || Math.floor(distAt(opp.alive ? t : opp.crashT) / su));
      scoresRef.current = { me: meScore, opp: oppScore };
      setScores({ me: meScore, opp: oppScore });

      draw();

      // finish detection
      const oppDone = mode === "solo" ? !opp.alive : (pub?.opp_done || !opp.alive);
      if (!me.alive && !oppDone && !crashedWaitRef.current) { crashedWaitRef.current = true; setCrashedWait(true); }
      if (!finishedRef.current && !me.alive && oppDone) {
        finishedRef.current = true;
        finalize(meScore, oppScore);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [view]); // eslint-disable-line

  const jumpsRef = useRef([]);
  const countRef = useRef(null);
  const scoresRef = useRef({ me: 0, opp: 0 });
  const tap = () => {
    const now = Date.now();
    const t = (now - startMsRef.current) / 1000;
    if (t < 0) return;
    const me = meRef.current;
    if (me && me.alive && me.ground) { jumpsRef.current.push(t); }
    sfx.click();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.code === "Space" && viewRef.current === "race") { e.preventDefault(); tap(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line

  const reconstructOpp = (opp, t) => {
    // re-sim opp from server jumps each frame is heavy; instead advance incrementally using oppJumps
    if (!opp.alive) return;
    if (pub?.opp_crash_t && t >= pub.opp_crash_t) { opp.alive = false; opp.crashT = pub.opp_crash_t; return; }
    stepDino(opp, t, oppJumpsRef.current, "me");
  };
  const oppJumpsRef = useRef([]);

  // ---- versus polling ----
  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const me = meRef.current;
        const { data } = await api.post(`/child/dino/${gameIdRef.current}/state`, {
          jumps: jumpsRef.current, score: scores.me, done: !me.alive, crash_t: me.crashT,
        });
        setPub(data);
        oppJumpsRef.current = data.opp_jumps || [];
        oppRef.current.serverScore = data.opp_score || 0;
        if (data.result) { setResult(data.result); }
      } catch {}
    }, 650);
  };
  const stopPolling = () => { clearInterval(pollRef.current); pollRef.current = 0; };

  const finalize = async (meScore, oppScore) => {
    stopPolling();
    if (mode === "solo") {
      try {
        const { data } = await api.post(`/child/dino/${gameIdRef.current}/state`, { jumps: jumpsRef.current, score: meScore, done: true, crash_t: meRef.current.crashT });
        setResult({ ...(data.result || {}), winner: meScore >= oppScore ? "JIJ" : "COMPUTER", meScore, oppScore });
      } catch (e) { setResult({ winner: meScore >= oppScore ? "JIJ" : "COMPUTER", meScore, oppScore }); }
    } else {
      // ensure final state sent
      try {
        const { data } = await api.post(`/child/dino/${gameIdRef.current}/state`, { jumps: jumpsRef.current, score: meScore, done: true, crash_t: meRef.current.crashT });
        setPub(data);
        setResult({ ...(data.result || {}), meScore, oppScore: data.opp_score ?? oppScore, winner: (data.result?.winner) });
      } catch (e) {}
    }
    sfx.win();
    setTimeout(() => { setView("result"); }, 700);
  };

  // ---- flows ----
  const beginCountdown = (data) => {
    seedRef.current = data.seed; gameIdRef.current = data.game_id;
    offsetRef.current = (data.server_now * 1000) - Date.now();
    startMsRef.current = (data.start_at * 1000) - offsetRef.current;
    jumpsRef.current = []; oppJumpsRef.current = [];
    resetRace();
    setView("race");
    if (data.mode === "versus") startPolling();
  };

  const startSolo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/child/dino/new", { mode: "solo" });
      gameIdRef.current = data.game_id; seedRef.current = data.seed; setMode("solo"); setPub(data);
      const r = await api.post(`/child/dino/${data.game_id}/ready`);
      beginCountdown(r.data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const createVersus = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/child/dino/new", { mode: "versus" });
      gameIdRef.current = data.game_id; seedRef.current = data.seed; setMode("versus"); setPub(data); setView("lobby");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const doJoin = async (code) => {
    setBusy(true); setView("loading");
    try {
      const { data } = await api.get(`/child/dino/join/${code.trim().toUpperCase()}`);
      if (data.not_found) { toast.error("Geen race gevonden met die code"); setView("menu"); setBusy(false); return; }
      gameIdRef.current = data.game_id; seedRef.current = data.seed; setMode("versus"); setPub(data); setView("lobby");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setView("menu"); }
    setBusy(false);
  };

  // lobby polling (versus, wait for both ready -> start_at)
  useEffect(() => {
    if (view !== "lobby") return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/child/dino/${gameIdRef.current}`);
        setPub(data);
        if (data.start_at) { clearInterval(id); beginCountdown(data); }
      } catch {}
    }, 900);
    return () => clearInterval(id);
  }, [view]); // eslint-disable-line

  const pressReady = async () => {
    try { const { data } = await api.post(`/child/dino/${gameIdRef.current}/ready`); setPub(data); if (data.start_at) beginCountdown(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const openBoard = async () => {
    setView("board");
    try { const { data } = await api.get("/child/dino-leaderboard"); setBoard(data); } catch {}
  };

  useEffect(() => () => { stopPolling(); cancelAnimationFrame(rafRef.current); }, []);

  const muted = { color: "var(--t-muted)" };

  // ---------- MENU ----------
  if (view === "menu")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-8 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" onClick={() => navigate("/leerling/speelhoek")} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="dino-back"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-heading text-2xl font-black themed-heading">Dinorace 🦖</h1>
          </div>
          <p className="font-semibold mb-6" style={muted}>Tik om te springen en ontwijk de cactussen en vogels! Het gaat steeds sneller. Wie komt het verst in deze head-to-head race?</p>

          <button onClick={startSolo} disabled={busy} data-testid="dino-solo-btn" className="w-full mb-3 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition text-left" style={{ background: "var(--t-surface)", borderColor: "var(--t-accent)" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Cpu className="w-7 h-7" /></div>
            <div className="flex-1"><h3 className="font-heading text-lg font-extrabold themed-heading">Race tegen de computer</h3><p className="text-sm font-semibold" style={muted}>Versla de computer-dino in een split-screen race!</p></div>
          </button>

          <button onClick={createVersus} disabled={busy} data-testid="dino-versus-btn" className="w-full mb-3 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition text-left" style={{ background: "var(--t-surface)", borderColor: "var(--t-primary)" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><Users className="w-7 h-7" /></div>
            <div className="flex-1"><h3 className="font-heading text-lg font-extrabold themed-heading">Daag een vriend uit</h3><p className="text-sm font-semibold" style={muted}>Deel een link, allebei klaar → 3-2-1 GO en racen maar!</p></div>
          </button>

          <ThemedCard className="p-5 mb-3">
            <h3 className="font-heading font-extrabold mb-2 flex items-center gap-2 themed-heading"><KeyRound className="w-5 h-5" style={{ color: "var(--t-accent)" }} /> Doe mee met een code</h3>
            <div className="flex gap-2">
              <input value={joinInput} onChange={(e) => setJoinInput(e.target.value.toUpperCase())} placeholder="bijv. K7M2P" maxLength={6} data-testid="dino-join-input" className="flex-1 h-12 rounded-xl px-4 font-heading font-black tracking-widest text-lg border-2 outline-none" style={{ background: "var(--t-surface2)", borderColor: "var(--t-border)", color: "var(--t-text)" }} />
              <Button onClick={() => joinInput.trim() && doJoin(joinInput)} disabled={busy || !joinInput.trim()} data-testid="dino-join-btn" className="h-12 rounded-xl font-bold border-0 px-5" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}>Mee doen</Button>
            </div>
          </ThemedCard>

          <Button onClick={openBoard} data-testid="dino-board-btn" className="w-full h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Trophy className="w-4 h-4 mr-1.5" style={{ color: "var(--t-accent)" }} /> Topscores</Button>
        </div>
      </ThemedShell>
    );

  if (view === "loading")
    return <ThemedShell><div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full border-4 animate-spin" style={{ borderColor: "var(--t-surface2)", borderTopColor: "var(--t-accent)" }} /></div></ThemedShell>;

  // ---------- LOBBY ----------
  if (view === "lobby") {
    const url = `${window.location.origin}/leerling/dinorace?code=${pub?.share_code}`;
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-10 pb-16 text-center">
          <div className="text-5xl mb-3">🦖🏁</div>
          <h1 className="font-heading text-2xl font-black themed-heading">Race-lobby</h1>
          <p className="font-semibold mt-2 mb-5" style={muted}>{pub?.opp_name ? "Allebei op 'Klaar' tikken om te starten!" : "Deel de link en wacht op je vriend…"}</p>

          <ThemedCard className="p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-heading font-extrabold themed-heading">{pub?.me_name || "Jij"}</span>
              <span className="text-sm font-bold px-2 py-1 rounded-full" style={{ background: pub?.me_ready ? "var(--t-success)" : "var(--t-surface2)", color: pub?.me_ready ? "#fff" : "var(--t-muted)" }}>{pub?.me_ready ? "KLAAR ✓" : "wacht"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-heading font-extrabold themed-heading">{pub?.opp_name || "wachten op speler…"}</span>
              <span className="text-sm font-bold px-2 py-1 rounded-full" style={{ background: pub?.opp_ready ? "var(--t-success)" : "var(--t-surface2)", color: pub?.opp_ready ? "#fff" : "var(--t-muted)" }}>{pub?.opp_ready ? "KLAAR ✓" : "wacht"}</span>
            </div>
          </ThemedCard>

          {pub?.share_code && (
            <ThemedCard className="p-5 mb-4">
              <p className="text-sm font-bold mb-1" style={muted}>Code</p>
              <div className="font-heading text-4xl font-black tracking-[0.3em] themed-heading" data-testid="dino-share-code">{pub.share_code}</div>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => { navigator.clipboard?.writeText(pub.share_code); toast.success("Code gekopieerd!"); }} data-testid="dino-copy-code" className="flex-1 h-10 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Copy className="w-4 h-4 mr-1.5" /> Code</Button>
                <Button onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link gekopieerd!"); }} data-testid="dino-copy-link" className="flex-1 h-10 rounded-xl font-bold border-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Share2 className="w-4 h-4 mr-1.5" /> Link</Button>
              </div>
            </ThemedCard>
          )}

          <Button onClick={pressReady} disabled={pub?.me_ready} data-testid="dino-ready-btn" className="w-full h-13 py-6 rounded-2xl font-bold text-lg border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>{pub?.me_ready ? "Wachten op tegenstander…" : "Ik ben klaar! 🏁"}</Button>
        </div>
      </ThemedShell>
    );
  }

  // ---------- RESULT ----------
  if (view === "result") {
    const meWon = result?.winner === "JIJ" || (result?.winner && result.winner === pub?.me_name);
    const tie = !result?.winner;
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-12 pb-16 text-center">
          <div className="text-6xl mb-2">{meWon ? "🏆" : tie ? "🤝" : "🦖"}</div>
          <h1 className="font-heading text-3xl font-black themed-heading">{meWon ? "Gewonnen!" : tie ? "Gelijkspel!" : "Verloren…"}</h1>
          <div className="flex justify-center gap-6 mt-5">
            <div><p className="text-sm font-bold" style={muted}>Jij</p><p className="font-heading text-4xl font-black themed-heading" data-testid="dino-me-score">{result?.meScore ?? scores.me}</p></div>
            <div className="font-heading text-3xl font-black self-center" style={muted}>vs</div>
            <div><p className="text-sm font-bold" style={muted}>{mode === "solo" ? "Computer" : (pub?.opp_name || "Vriend")}</p><p className="font-heading text-4xl font-black themed-heading" data-testid="dino-opp-score">{result?.oppScore ?? scores.opp}</p></div>
          </div>
          {result && result.xp_earned != null && (
            <div className="flex justify-center gap-4 mt-5">
              <span className="font-heading font-extrabold" style={{ color: "var(--t-accent)" }}>+{result.xp_earned} XP</span>
              <span className="font-heading font-extrabold" style={{ color: "var(--t-primary)" }}>+{result.coins_earned} 🪙</span>
            </div>
          )}
          <div className="flex flex-col gap-2 mt-7">
            <Button onClick={startSolo} data-testid="dino-again-btn" className="h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><RotateCcw className="w-4 h-4 mr-1.5" /> Nog een race (vs computer)</Button>
            <Button onClick={openBoard} className="h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Trophy className="w-4 h-4 mr-1.5" style={{ color: "var(--t-accent)" }} /> Topscores</Button>
            <Button onClick={() => navigate("/leerling/speelhoek")} className="h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Home className="w-4 h-4 mr-1.5" /> Speelhoek</Button>
          </div>
        </div>
      </ThemedShell>
    );
  }

  // ---------- BOARD ----------
  if (view === "board")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-8 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" onClick={() => setView("menu")} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="dino-board-back"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-heading text-2xl font-black themed-heading">Dino Topscores 🏆</h1>
          </div>
          <ThemedCard className="p-4" data-testid="dino-board">
            {!board ? <p className="text-center py-6" style={muted}>Laden…</p> : board.top.length === 0 ? <p className="text-center py-6" style={muted}>Nog geen scores. Wees de eerste!</p> :
              board.top.map((r, i) => (
                <div key={i} className="flex items-center gap-3 px-2 py-2.5 rounded-xl" style={{ background: r.me ? "var(--t-surface2)" : "transparent" }}>
                  <span className="w-7 font-heading font-black text-lg" style={{ color: i === 0 ? "#f4c40f" : i === 1 ? "#b8c0cc" : i === 2 ? "#d08b50" : "var(--t-muted)" }}>{i + 1}</span>
                  <Medal className="w-4 h-4" style={{ color: i < 3 ? "var(--t-accent)" : "var(--t-muted)" }} />
                  <span className="flex-1 font-bold">{r.name}{r.me ? " (jij)" : ""}</span>
                  <span className="font-heading font-black">{r.best}</span>
                </div>
              ))}
          </ThemedCard>
        </div>
      </ThemedShell>
    );

  // ---------- RACE ----------
  return (
    <div className="fixed inset-0 overflow-hidden select-none" style={{ background: "#000" }} data-testid="dino-stage">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full touch-none" onPointerDown={(e) => { e.preventDefault(); tap(); }} data-testid="dino-canvas" />
      {countLabel && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <motion.div key={countLabel} initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="font-heading text-8xl font-black text-white drop-shadow-[0_4px_0_rgba(0,0,0,0.5)]">{countLabel}</motion.div>
        </div>
      )}
      {crashedWait && !countLabel && (
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-none px-6">
          <div className="px-5 py-3 rounded-2xl text-center" style={{ background: "rgba(0,0,0,0.55)" }}>
            <p className="font-heading text-xl font-black text-white">Jij bent af! 🦖💥</p>
            <p className="text-white/80 font-bold text-sm mt-1">{mode === "solo" ? "De computer" : (pub?.opp_name || "Je tegenstander")} racet nog…</p>
          </div>
        </div>
      )}
      <button onClick={() => { stopPolling(); cancelAnimationFrame(rafRef.current); navigate("/leerling/speelhoek"); }} className="absolute top-4 left-4 w-10 h-10 rounded-full flex items-center justify-center z-10" style={{ background: "rgba(0,0,0,0.35)" }} data-testid="dino-stage-back"><ArrowLeft className="w-5 h-5 text-white" /></button>
      <p className="absolute bottom-4 inset-x-0 text-center text-white/70 text-sm font-bold pointer-events-none">Tik om te springen 👆</p>
    </div>
  );
}
